import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import TableRestaurantOutlinedIcon from '@mui/icons-material/TableRestaurantOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { env } from '@/config';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

const BRAND_FEATURES = [
  { icon: EventAvailableOutlinedIcon, key: 'reservations' },
  { icon: TableRestaurantOutlinedIcon, key: 'tables' },
  { icon: GroupsOutlinedIcon, key: 'guests' },
] as const;

/** Soft blurred color orbs behind the brand panel content — decorative only. */
function AuroraOrbs() {
  const theme = useTheme();

  const orb = (color: string, size: number, top: string, left: string, blur: number) => ({
    position: 'absolute' as const,
    top,
    left,
    width: size,
    height: size,
    borderRadius: '50%',
    background: color,
    filter: `blur(${blur}px)`,
  });

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }} aria-hidden="true">
      <Box sx={orb(alpha(theme.palette.primary.main, 0.35), 340, '-12%', '-10%', 90)} />
      <Box sx={orb(alpha(theme.palette.secondary.main, 0.28), 300, '52%', '58%', 100)} />
      <Box sx={orb(alpha(theme.palette.primary.main, 0.18), 220, '68%', '-8%', 80)} />
    </Box>
  );
}

export function AuthLayout() {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <Box
        component="aside"
        sx={{
          position: 'relative',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '44%',
          maxWidth: 620,
          overflow: 'hidden',
          p: { md: 6, lg: 8 },
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        <AuroraOrbs />

        <Box
          component="img"
          src="/logos/logo-main.svg"
          alt={env.appName}
          sx={{ position: 'relative', height: 40, width: 'auto', alignSelf: 'flex-start' }}
        />

        <Stack spacing={4} sx={{ position: 'relative' }}>
          <Stack spacing={1.5}>
            <Typography variant="h4" sx={{ fontWeight: 700, maxWidth: 420 }}>
              {t('auth.brandPanel.tagline')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
              {t('auth.brandPanel.subtitle')}
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {BRAND_FEATURES.map(({ icon: Icon, key }) => (
              <Stack key={key} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t(`auth.brandPanel.features.${key}`)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ position: 'relative' }}>
          {t('auth.brandPanel.footer', { year: new Date().getFullYear() })}
        </Typography>
      </Box>

      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: { xs: 'space-between', md: 'flex-end' }, p: { xs: 2, sm: 3 } }}
        >
          <Box
            component="img"
            src="/logos/logo-main.svg"
            alt={env.appName}
            sx={{ display: { xs: 'block', md: 'none' }, height: 32, width: 'auto' }}
          />
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <LanguageSwitcher />
            <ThemeToggle />
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 4 } }}>
          <Container maxWidth="xs" disableGutters>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4.5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: (theme) =>
                  theme.palette.mode === 'light' ? '0 24px 48px -24px rgba(20, 20, 43, 0.18)' : 'none',
              }}
            >
              <Outlet />
            </Paper>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
